// ===== Imports Start =====
import React, { useEffect, useRef, useState } from "react";
import { FiDownload, FiLogOut, FiMail, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";
import ProfileImage from "./ProfileImage";
import { useUserProfile } from "../../context/UserProfileContext";
import ExportHistoryModal from "./ExportHistoryModal";
import MailPreferencesOffcanvas from "./MailPreferencesOffcanvas";
import axiosInstance, { getUserRole } from "../../API/interceptor";
// ===== Imports End =====

// ===== DropdownUser Component Start =====
const DropdownUser: React.FC = () => {
  const { profile } = useUserProfile();
  const [dropdownOpen,    setDropdownOpen]    = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [mailPrefOpen,    setMailPrefOpen]    = useState(false);
  const [freshPhoto,      setFreshPhoto]      = useState<string | null>(null);

  const trigger = useRef<any>(null);
  const dropdown = useRef<any>(null);

  // ===== Fetch Fresh Profile Photo Start =====
  // profile.profile_image is a presigned S3 URL that expires after 1 hour.
  // Fetch a fresh base64 photo from the API instead — it never expires.
  useEffect(() => {
    getUserRole()
      .then((roleRes: any) => {
        const staffId = roleRes.data?.user_id;
        if (!staffId) return Promise.reject("no staffId");
        return axiosInstance.get(`/task_assignment/get_user_photo/${staffId}`);
      })
      .then((photoRes: any) => {
        if (photoRes?.data?.success && photoRes?.data?.photo) {
          setFreshPhoto(photoRes.data.photo);
        }
      })
      .catch(() => {});
  }, []);
  // ===== Fetch Fresh Profile Photo End =====

  // ===== Click Outside Handler Start =====
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });
  // ===== Click Outside Handler End =====

  // ===== Escape Key Handler Start =====
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });
  // ===== Escape Key Handler End =====

  // ===== Logout Handler Start =====
  const handleLogout = () => {
    window.location.href = process.env.REACT_APP_REDIRECT_URL + "/";;
  };
  // ===== Logout Handler End =====

  // ===== Component Render Start =====
  return (
    <>
    <div className="relative">
      <Link
        ref={trigger}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-4"
        to="#"
      >
        <span className="h-10 w-10 rounded-full overflow-hidden block flex-shrink-0">
          {/* ✅ Fresh base64 photo from API (never expires) */}
          {freshPhoto ? (
            <img
              src={freshPhoto}
              alt={profile?.alias_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <ProfileImage />
          )}
        </span>
      </Link>

      <div
        ref={dropdown}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        className={`absolute right-0 mt-4 flex w-60 flex-col rounded bg-white drop-shadow ${
          dropdownOpen ? "block" : "hidden"
        }`}
      >
        {/* ✅ Dynamic user info */}
        <div className="py-2 px-4 border-b">
          <h5 className="font-medium">
            {profile?.alias_name ?? "Loading..."}
          </h5>
          <span className="block text-xs">
            {profile?.email ?? ""}
          </span>
        </div>

        <ul className="flex flex-col gap-5 p-4">
          {/* ===== Profile Link Start ===== */}
          <li className="text-nowrap">
            <a
              href={`${process.env.REACT_APP_REDIRECT_URL}/profile`}
              className="flex items-center gap-3 text-slate-700 duration-300 ease-in-out hover:text-primary"
            >
              <FiUser />
              Profile
            </a>
          </li>
          {/* ===== Profile Link End ===== */}

          {/* ===== Export History Start ===== */}
          <li className="text-nowrap">
            <button
              id="activity-log-link"
              onClick={() => { setDropdownOpen(false); setExportModalOpen(true); }}
              className="flex items-center gap-3 text-slate-700 duration-300 ease-in-out hover:text-primary w-full text-left"
            >
              <FiDownload />
              Export History
            </button>
          </li>
          {/* ===== Export History End ===== */}

          {/* ===== Email Preferences (Opt) Start ===== */}
          <li className="text-nowrap">
            <button
              id="mail-preferences-link"
              onClick={() => { setDropdownOpen(false); setMailPrefOpen(true); }}
              className="flex items-center gap-3 text-slate-700 duration-300 ease-in-out hover:text-primary w-full text-left"
            >
              <FiMail />
              Email Preferences
            </button>
          </li>
          {/* ===== Email Preferences (Opt) End ===== */}

          {/* ===== Sign Out Start ===== */}
          <li className="text-nowrap">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 text-slate-700 duration-300 ease-in-out hover:text-primary w-full text-left"
            >
              <FiLogOut />
              Logout
            </button>
          </li>
          {/* ===== Sign Out End ===== */}
        </ul>
      </div>
    </div>

    {/* ===== Export History Modal Start ===== */}
    <ExportHistoryModal
      isOpen={exportModalOpen}
      onClose={() => setExportModalOpen(false)}
    />
    {/* ===== Export History Modal End ===== */}

    {/* ===== Mail Preferences Offcanvas Start ===== */}
    <MailPreferencesOffcanvas
      isOpen={mailPrefOpen}
      onClose={() => setMailPrefOpen(false)}
    />
    {/* ===== Mail Preferences Offcanvas End ===== */}
  </>
  );
};
// ===== DropdownUser Component End =====

export default DropdownUser;